import React, { useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useAdvertiser, useDeleteAdvertiser } from "@/hooks/use-advertisers";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/Card";
import { EntityLabels } from "@/components/EntityLabels";
import { formatDate, cn } from "@/lib/utils";
import { Trash2, ExternalLink, Settings, Tag } from "lucide-react";

const tabs = [
  { id: "details", label: "Details", icon: Settings },
  { id: "labels", label: "Labels", icon: Tag },
] as const;

type TabId = (typeof tabs)[number]["id"];

export function AdvertiserDetailPage() {
  const { id } = useParams({ strict: false });
  const navigate = useNavigate();
  const { data, isLoading } = useAdvertiser(id!);
  const deleteMutation = useDeleteAdvertiser();
  const [activeTab, setActiveTab] = useState<TabId>("details");

  const advertiser = data?.data;

  async function handleDelete() {
    if (!advertiser) return;
    if (!window.confirm(`Are you sure you want to delete "${advertiser.name}"?`)) return;
    await deleteMutation.mutateAsync(advertiser.id);
    navigate({ to: "/advertisers" });
  }

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Loading..." backHref="/advertisers" />
        <div className="animate-pulse space-y-4">
          <div className="h-48 bg-gray-100 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!advertiser) {
    return (
      <div>
        <PageHeader title="Advertiser Not Found" backHref="/advertisers" />
        <p className="text-gray-500">This advertiser does not exist or has been removed.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <PageHeader
          title={advertiser.name}
          description={`Advertiser #${advertiser.id}`}
          backHref="/advertisers"
        />
        <button
          onClick={handleDelete}
          disabled={deleteMutation.isPending}
          className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 text-sm font-medium px-3 py-2 rounded-lg hover:bg-red-50 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          Delete
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors",
                  activeTab === tab.id
                    ? "border-gray-900 text-gray-900"
                    : "border-transparent text-gray-500 hover:text-gray-700",
                )}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === "details" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card title="Details">
              <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Status</dt>
                  <dd className="mt-1"><StatusBadge status={advertiser.status} /></dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Currency</dt>
                  <dd className="mt-1 text-sm text-gray-700">{advertiser.defaultCurrencyId}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Attribution Method</dt>
                  <dd className="mt-1 text-sm text-gray-700 capitalize">{advertiser.attributionMethod?.replace(/_/g, " ")}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Platform</dt>
                  <dd className="mt-1 text-sm text-gray-700">{advertiser.platformName || "None"}</dd>
                </div>
                {advertiser.platformUrl && (
                  <div className="col-span-2">
                    <dt className="text-xs font-medium text-gray-500 uppercase">Platform URL</dt>
                    <dd className="mt-1">
                      <a
                        href={advertiser.platformUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700"
                      >
                        {advertiser.platformUrl}
                        <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                      </a>
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Account Manager</dt>
                  <dd className="mt-1 text-sm text-gray-700">{advertiser.accountManagerId ? `#${advertiser.accountManagerId}` : "None"}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Sales Manager</dt>
                  <dd className="mt-1 text-sm text-gray-700">{advertiser.salesManagerId ? `#${advertiser.salesManagerId}` : "None"}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Created</dt>
                  <dd className="mt-1 text-sm text-gray-700">{formatDate(advertiser.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Updated</dt>
                  <dd className="mt-1 text-sm text-gray-700">{formatDate(advertiser.updatedAt)}</dd>
                </div>
              </dl>
            </Card>
          </div>

          <div>
            {advertiser.internalNotes && (
              <Card title="Internal Notes">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{advertiser.internalNotes}</p>
              </Card>
            )}
          </div>
        </div>
      )}

      {activeTab === "labels" && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <EntityLabels
            entityType="advertiser"
            entityId={id!}
            apiBase="/v1/advertisers"
          />
        </div>
      )}
    </div>
  );
}
